import { Add, Clear } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  TextField,
} from '@mui/material';
import { Box } from '@mui/system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import GlnDivider from './GlnDivider';
import { CompanyLocalizationDataOutSchema } from '../types';
import { useGlnCodes } from '../hooks';
import NumberField from '../NumberField';

interface GlnCodeWithDistance {
  glnCode: CompanyLocalizationDataOutSchema;
  distanceToNext?: number;
}

interface Props {
  open: boolean;
  initialGlnCodes?: string[];
  initialGlnCodesMultiplier?: number;
  onSubmit: (
    glnCodes: string[],
    glnCodesMultiplier: number,
    distance: number
  ) => void;
  onCancel: () => void;
}

export default function GlnDistanceDialog({
  open,
  initialGlnCodes = [],
  initialGlnCodesMultiplier,
  onSubmit,
  onCancel,
}: Readonly<Props>) {
  const [newCode, setNewCode] = React.useState<string>('');
  const { t } = useTranslation('components', { keyPrefix: 'glnCodes' });

  const [glnCodes, setGlnCodes] = React.useState<GlnCodeWithDistance[]>([]);
  const [glnCodesMultiplier, setGlnCodesMultiplier] = React.useState<number>(
    initialGlnCodesMultiplier || 1
  );

  const { fetchGlnCode, fetchDistances } = useGlnCodes();

  const handleInitialGlnCodes = () => {
    if (initialGlnCodes.length > 0) {
      fetchDistances
        .mutateAsync(initialGlnCodes)
        .then((response) => {
          const { data } = response;
          setGlnCodes([
            ...data.map((entry) => ({
              glnCode: entry.gln_code_from,
              distanceToNext: entry.distance_km,
            })),
            { glnCode: data[data.length - 1].gln_code_to },
          ]);
        })
        .catch(() => {});
    }
  };

  React.useEffect(() => {
    if (open) {
      handleInitialGlnCodes();
      setGlnCodesMultiplier(initialGlnCodesMultiplier || 1);
    } else {
      setGlnCodes([]);
      setNewCode('');
      setGlnCodesMultiplier(1);
    }
  }, [initialGlnCodes, initialGlnCodesMultiplier, open]);

  const addGlnCode = () => {
    if (glnCodes.length === 0) {
      fetchGlnCode.mutateAsync(newCode).then((response) => {
        const { data } = response;
        if (data.length === 0) {
          toast.error(t('notFound') as string);
        } else {
          setGlnCodes([{ glnCode: data[0] }]);
          setNewCode('');
        }
      });
    } else {
      fetchDistances
        .mutateAsync([glnCodes[glnCodes.length - 1].glnCode.gln_code, newCode])
        .then((response) => {
          const { data } = response;
          setGlnCodes((prev) => [
            ...prev.slice(0, prev.length - 1),
            {
              glnCode: data[0].gln_code_from,
              distanceToNext: data[0].distance_km,
            },
            { glnCode: data[0].gln_code_to },
          ]);
          setNewCode('');
        })
        .catch(() => {});
    }
  };

  const deleteGlnCode = (index: number) => {
    if (index === 0) {
      setGlnCodes((prev) => [...prev.slice(1)]);
    } else if (index === glnCodes.length - 1) {
      setGlnCodes((prev) => [
        ...prev.slice(0, -2),
        { glnCode: prev[index].glnCode },
      ]);
    } else {
      fetchDistances
        .mutateAsync([
          glnCodes[index - 1].glnCode.gln_code,
          glnCodes[index + 1].glnCode.gln_code,
        ])
        .then((response) => {
          const { data } = response;
          setGlnCodes((prev) => [
            ...prev.slice(0, index - 1),
            {
              glnCode: data[0].gln_code_from,
              distanceToNext: data[0].distance_km,
            },
            ...prev.slice(index + 1),
          ]);
        })
        .catch(() => {});
    }
  };

  const isLoading = fetchGlnCode.isLoading || fetchDistances.isLoading;

  const totalDistance =
    Math.round(
      glnCodes.reduce((acc, gln) => acc + (gln.distanceToNext || 0), 0) *
        glnCodesMultiplier
    ) || 0;

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>{t('dialogTitle')}</DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: '16px' }}>
          <TextField
            label={t('addNewCode')}
            value={newCode}
            onChange={(e) => setNewCode(e.target.value)}
            disabled={isLoading}
            fullWidth
            sx={{ mt: '8px' }}
          />
          <Button startIcon={<Add />} onClick={addGlnCode} disabled={isLoading}>
            {t('addButton')}
          </Button>
        </Box>
        {glnCodes.map(({ glnCode, distanceToNext }, index) => (
          <React.Fragment key={index}>
            <List sx={{ p: 0 }}>
              <ListItem
                secondaryAction={
                  <IconButton onClick={() => deleteGlnCode(index)}>
                    <Clear />
                  </IconButton>
                }
              >
                <Box>
                  <DialogContentText>GLN: {glnCode.gln_code}</DialogContentText>
                  <DialogContentText variant="caption">
                    {glnCode.address}
                  </DialogContentText>
                  <DialogContentText variant="subtitle2">
                    {glnCode.company_name}
                  </DialogContentText>
                </Box>
              </ListItem>
            </List>
            {distanceToNext !== undefined && (
              <>
                <GlnDivider />
                <DialogContentText>{distanceToNext} km</DialogContentText>
                <GlnDivider />
              </>
            )}
          </React.Fragment>
        ))}
        <NumberField
          sx={{ mt: '24px', mb: '12px' }}
          fullWidth
          decimalPlaces={2}
          label={t('multiplier')}
          value={glnCodesMultiplier}
          onChange={(value) => setGlnCodesMultiplier(value as number)}
        />
        {glnCodes.length >= 2 && (
          <DialogContentText>
            {totalDistance} km {t('totalDistance')}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          width="100%"
        >
          <Button onClick={onCancel} variant="secondary" disabled={isLoading}>
            {t('cancelButton')}
          </Button>
          <Button
            onClick={() => {
              const glnCodesValues = glnCodes.map(
                ({ glnCode }) => glnCode.gln_code
              );
              toast.success(t('success') as string);
              onSubmit(glnCodesValues, glnCodesMultiplier, totalDistance);
            }}
            disabled={totalDistance === 0 || isLoading}
          >
            {t('confirmButton')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
